<script lang="ts">
	//Services
	import { push } from 'svelte-spa-router';
	//Stores
	import { edit } from 'src/stores/ui/edit_lists';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	//Helpers
	import { trimLastChar } from 'src/helpers/utilities';
	import {
		isLegacyCameraButtonCardLayout,
		isSpecialDashCameraLayout,
		normalizeCameraConfig,
	} from 'src/components/Dashboards/dash-camera-helpers';
	import type { DashCameraWidgetConfig, DashWidgetTypes } from '../dash-types';
	//Components
	import DashWeatherWidgetCard from './Components/DashWeatherWidgetCard.svelte';
	import DashAccInfoWidgetCard from './Components/DashAccInfoWidgetCard.svelte';
	import DashRoomWidgetCard from './Components/DashRoomWidgetCard.svelte';
	import DashGroupWidgetCard from './Components/DashGroupWidgetCard.svelte';
	import DashDeviceControlCard from './Components/DashDeviceWidgetCard/DashDeviceControlCard.svelte';
	import DashMediaWidgetCard from './Components/DashMediaWidgetCard.svelte';
	import DashChartWidgetCard from './Components/DashChartWidgetCard.svelte';
	import DashSceneWidgetCard from './Components/DashSceneWidgetCard.svelte';
	import DashAlarmWidgetCard from './Components/DashAlarmWidgetCard.svelte';
	import DashThermostatWidgetCard from './Components/DashThermostatWidgetCard.svelte';
	import DashLightWidgetCard from './Components/DashLightWidgetCard.svelte';
	import DashDeviceQuickControl from './Components/DashDeviceWidgetCard/DashDeviceQuickControl.svelte';
	import DashDeviceCard from './Components/DashDeviceWidgetCard/DashDeviceCard.svelte';
	import DashCameraWidgetCard from './Components/DashDeviceWidgetCard/DashCameraWidgetCard.svelte';

	export let cards: any;
	export let type: DashWidgetTypes;
	export let cameraConfig: DashCameraWidgetConfig = {};

	$: normalizedCameraConfig = normalizeCameraConfig(cameraConfig);
	$: cameraVariant = normalizedCameraConfig.variant;
	$: useSpecialCameraLayout = type === 'dashCamera' && isSpecialDashCameraLayout(cameraVariant);
	$: useLegacyCameraButtonCards =
		type === 'dashCamera' && isLegacyCameraButtonCardLayout(cameraVariant);

	const componentMap: Record<
		Exclude<DashWidgetTypes, 'dashCamera'>,
		{ component: any; getProps: (card: any) => Record<string, any>; noIteration?: boolean }
	> = {
		dashInfo: { component: DashAccInfoWidgetCard, getProps: () => ({}), noIteration: true },
		dashGroup: { component: DashGroupWidgetCard, getProps: (card) => ({ groupWidget: card }) },
		dashScene: { component: DashSceneWidgetCard, getProps: (card) => ({ sceneWidget: card }) },
		dashAlarm: { component: DashAlarmWidgetCard, getProps: (card) => ({ alarmWidget: card }) },
		dashParameterDev: {
			// component: DashDeviceParamCard,
			component: DashDeviceQuickControl,
			getProps: (card) => ({ deviceWidget: card }),
		},
		dashControlDev: {
			component: DashDeviceControlCard,
			getProps: (card) => ({ deviceWidget: card }),
		},
		dashQuickDev: {
			component: DashDeviceQuickControl,
			getProps: (card) => ({ deviceWidget: card, cameraConfig }),
		},
		dashCardDev: {
			component: DashDeviceCard,
			getProps: (card) => ({ deviceWidget: card, cameraConfig }),
		},
		dashRoom: { component: DashRoomWidgetCard, getProps: (card) => ({ roomWidget: card }) },
		dashWeather: { component: DashWeatherWidgetCard, getProps: () => ({}) },
		dashChart: { component: DashChartWidgetCard, getProps: () => ({}) },
		dashMedia: { component: DashMediaWidgetCard, getProps: (card) => ({ mediaWidget: card }) },
		dashThermostat: {
			component: DashThermostatWidgetCard,
			getProps: (card) => ({ thermostatWidget: card }),
		},
		dashLight: { component: DashLightWidgetCard, getProps: (card) => ({ lightWidget: card }) },
	};

	const onWidgetClick = (card, event: Event = null) => {
		if ($edit) return;
		if ((event?.target as Element)?.closest?.('[data-dashboard-widget-interactive]')) return;

		const id = card.id;
		let type = trimLastChar(card._type);
		type = type === 'temp_scheme' ? 'thermostat' : type;

		if (card._type === 'chart') {
			push(`/cons/0`);
			setSidePanelState({ open: false });
		} else if (type == 'room') {
			push(`/home/${type}/${id}/devices`);
			setSidePanelState({ open: false });
		} else {
			setSidePanelState({ type, id, open: true });
		}
	};
</script>

{#if type === 'dashCamera'}
	{#if useSpecialCameraLayout}
		<DashCameraWidgetCard {cards} {cameraConfig} />
	{:else if useLegacyCameraButtonCards}
		{#each cards as card}
			<DashDeviceCard
				deviceWidget={card}
				{cameraConfig}
				editMode={$edit}
				showFullscreen={!$edit}
				on:click={(event) => onWidgetClick(card, event)}
				on:arrow-click={() => onWidgetClick(card)}
				on:fullscreen={() => onWidgetClick(card)}
				on:handle-drag-start
				on:handle-drag-end
			/>
		{/each}
	{/if}
{:else if componentMap[type]}
	{#if componentMap[type].noIteration}
		<svelte:component this={componentMap[type].component} />
	{:else}
		{#each cards as card}
			<svelte:component
				this={componentMap[type].component}
				{...componentMap[type].getProps(card)}
				editMode={$edit}
				showFullscreen={!$edit}
				on:click={(event) => onWidgetClick(card, event)}
				on:arrow-click={() => onWidgetClick(card)}
				on:fullscreen={() => onWidgetClick(card)}
				on:handle-drag-start
				on:handle-drag-end
			/>
		{/each}
	{/if}
{/if}
